import { useAuth } from '@clerk/nextjs';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { AppSyncGraphQLError, StaleTime } from 'queries/types';

import { safeTier } from 'components/@loyalty/utils';

import { accountQuery } from './queries';

export const useAccount = () => {
  const { reload } = useRouter();

  const { isSignedIn, isLoaded: clerkIsLoaded } = useAuth();

  const { error, data, isPending } = useQuery({
    ...accountQuery,
    staleTime: StaleTime.MINUTE,
    retry: false,
    refetchOnWindowFocus: true,
    enabled: !!isSignedIn,
  });

  const graphqlError = (error as unknown as AppSyncGraphQLError)?.response
    ?.errors?.[0];

  // Edge case: after signIn, Clerk might not yet have the token, resulting in a Unauthorized error.
  // Reload the page to make sure the Clerk client is correctly initialized.
  useEffect(() => {
    if (graphqlError?.errorType === 'Unauthorized') {
      reload();
    }
  }, [graphqlError, reload]);

  // Was an edge case, mostly in safari where we got this error when refreshing the page multiple times.
  // Query should still be loading state because otherwise we get redirected to the onboarding page.
  const failedToLoadError =
    error instanceof TypeError && error.message === 'Load failed';

  return {
    account: data?.account,
    loyaltyPoints: data?.account?.loyalty?.points ?? 0,
    loyaltyTier: safeTier(data?.account?.loyalty?.tier),
    isLoading: !clerkIsLoaded || (isSignedIn && isPending) || failedToLoadError,
    isNotFound: graphqlError?.errorType === 'NotFound',
    isSignedIn: !!isSignedIn,
  };
};
